import type {
  ColumnDataType,
  DatabaseBlockModel,
  ParagraphBlockModel,
} from '@blocksuite/affine-model';
import {
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  type DatabaseShardManifestEntry,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import { getSelectedModelsCommand } from '@blocksuite/affine-shared/commands';
import { FeatureFlagService } from '@blocksuite/affine-shared/services';
import { type InsertToPosition } from '@blocksuite/affine-shared/utils';
import {
  assertDatabaseColumnShapeWithinLimits,
  type DatabaseFlags,
  type DatabaseProjectionQuery,
  type DatabaseProjectionSource,
  DataSourceBase,
  type DataSourceCellsWindow,
  type DataSourceCellsWindowRequest,
  type DataSourceRowsWindow,
  type DataSourceTableViewLayoutState,
  type DataSourceViewLayoutState,
  type DataSourceViewWindowState,
  type DataViewDataType,
  type PropertyMetaConfig,
  ROW_SHARDED_DATABASE_GATE_MESSAGE,
  type TypeInstance,
  type ViewManager,
  type ViewMeta,
} from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import type { EditorHost } from '@blocksuite/std';
import { type BlockModel, type Store } from '@blocksuite/store';
import { computed, type ReadonlySignal, signal } from '@preact/signals-core';
import {
  assertDatabaseRuntimeIdentity,
  FILE_INDEX_MANAGED_BY,
  type ResolvedDatabaseRuntimeIdentity,
} from '@streamlain/database-identity';

import { getIcon } from './block-icons.js';
import {
  type DatabaseRowCellValueInsert,
  type DatabaseRowShardDocLoader,
  type DatabaseRowShardDocLoadReason,
  type DatabaseRowShardReadiness,
  type DatabaseRowShardRepairResult,
  type DatabaseRowShardSnapshotBarrier,
  type DatabaseUiStatePersistence,
  type DataSourceWriteActor,
  type ReactiveShardedCellValue,
  type ShardedRowRecord,
  type ShardedRowsDocCacheEntry,
  type SpacialProperty,
} from './data-source-contracts.js';
import { DatabaseLocalUiController } from './data-source-local-ui.js';
import {
  provisionShardedDatabaseHostBlock,
  type ShardedDatabaseBlockDataSourceProvisionInput,
  type ShardedDatabaseBlockDataSourceProvisionResult,
} from './data-source-provisioning.js';
import { createDatabaseSourceRuntime } from './data-source-runtime.js';
import type {
  DatabaseShardDocument,
  DatabaseSourceRuntime,
} from './data-source-runtime-context.js';
import {
  hasLegacyInlineDatabaseStorageModel,
  resolveDatabaseStorageModel,
} from './data-source-storage.js';
import {
  FILE_INDEX_ROOT_SCHEMA_HARD_LIMIT_BYTES,
  FILE_INDEX_VIEW_HARD_LIMIT,
  INITIAL_DATABASE_ROW_WINDOW_LIMIT,
  isFileIndexManagedColumnData,
  isFileIndexSystemColumnId,
  isSystemManagedPropertyData,
  rowShardDocLoaders,
  rowShardSnapshotBarriers,
} from './data-source-values.js';
import { DatabaseBlockViewManager } from './data-source-view-manager.js';
import {
  getFileIndexShardRegistry,
  sortFileIndexShardEntries,
} from './file-index-shard-registry.js';
import { databaseBlockProperties } from './properties/index.js';
import { getOrderedViews, getViewDataForRead } from './utils/block-utils.js';
import {
  databaseBlockViewConverts,
  getCreatableDatabaseBlockViews,
} from './views/index.js';

export type { DatabaseRowShardDocLoadReason } from './data-source-contracts.js';
export type { DatabaseRowShardDocLoadContext } from './data-source-contracts.js';
export type { DatabaseRowShardHydrationProgress } from './data-source-contracts.js';
export type { DatabaseRowShardReadiness } from './data-source-contracts.js';
export type { DatabaseRowShardRepairResult } from './data-source-contracts.js';
export type { DatabaseRowShardDocLoader } from './data-source-contracts.js';
export type { DatabaseRowShardSnapshotBarrierContext } from './data-source-contracts.js';
export type { DatabaseRowShardSnapshotBarrier } from './data-source-contracts.js';
export type { DatabaseRowCellValueInsert } from './data-source-contracts.js';
export type { ShardedDatabaseRootCreationInput } from './data-source-provisioning.js';
export type { ShardedDatabaseReferenceProps } from './data-source-provisioning.js';
export type { ShardedDatabaseHostBlockProvisionInput } from './data-source-provisioning.js';
export type { ShardedDatabaseHostBlockProvisionResult } from './data-source-provisioning.js';
export type { ShardedDatabaseBlockDataSourceProvisionInput } from './data-source-provisioning.js';
export type { ShardedDatabaseBlockDataSourceProvisionResult } from './data-source-provisioning.js';
export { createShardedDatabaseReferenceForHostDoc } from './data-source-provisioning.js';
export { ensureShardedDatabaseRootForHostDoc } from './data-source-provisioning.js';
export { provisionShardedDatabaseHostBlock } from './data-source-provisioning.js';
export { resolveDatabaseStorageModel } from './data-source-storage.js';
export { isDatabaseStorageModelReady } from './data-source-storage.js';
export { observeDatabaseStorageModelReady } from './data-source-storage.js';

export class DatabaseBlockDataSource extends DataSourceBase {
  private readonly runtime = this.createRuntime();

  private createRuntime(): DatabaseSourceRuntime {
    const source = this;
    return createDatabaseSourceRuntime({
      get propertyRegistry() {
        return DatabaseBlockDataSource.propertiesMap;
      },
      get dataSource() {
        return source;
      },
      getUnshardedRowsWindow: request => super.getRowsWindow(request),
      get _model() {
        return source._model;
      },
      get _shardedRefreshFrame() {
        return source._shardedRefreshFrame;
      },
      set _shardedRefreshFrame(value) {
        source._shardedRefreshFrame = value;
      },
      get _shardedRowIds() {
        return source._shardedRowIds;
      },
      set _shardedRowIds(value) {
        source._shardedRowIds = value;
      },
      get _shardedRowsById() {
        return source._shardedRowsById;
      },
      set _shardedRowsById(value) {
        source._shardedRowsById = value;
      },
      get isRowSharded() {
        return source.isRowSharded;
      },
      get _shardedRowsRevision() {
        return source._shardedRowsRevision;
      },
      get isFileIndexDatabase() {
        return source.isFileIndexDatabase.bind(source);
      },
      get _rowShardEmptyConfirmedSnapshot() {
        return source._rowShardEmptyConfirmedSnapshot;
      },
      set _rowShardEmptyConfirmedSnapshot(value) {
        source._rowShardEmptyConfirmedSnapshot = value;
      },
      get databaseId() {
        return source.databaseId;
      },
      get storageDoc() {
        return source.storageDoc;
      },
      get _hostModel() {
        return source._hostModel;
      },
      get _disposed() {
        return source._disposed;
      },
      set _disposed(value) {
        source._disposed = value;
      },
      get readonly$() {
        return source.readonly$;
      },
      get _rowShardAutoRepairInFlight() {
        return source._rowShardAutoRepairInFlight;
      },
      set _rowShardAutoRepairInFlight(value) {
        source._rowShardAutoRepairInFlight = value;
      },
      get _rowShardEmptyConfirmationAttemptedSnapshot() {
        return source._rowShardEmptyConfirmationAttemptedSnapshot;
      },
      set _rowShardEmptyConfirmationAttemptedSnapshot(value) {
        source._rowShardEmptyConfirmationAttemptedSnapshot = value;
      },
      get _rowShardAutoRepairAbort() {
        return source._rowShardAutoRepairAbort;
      },
      set _rowShardAutoRepairAbort(value) {
        source._rowShardAutoRepairAbort = value;
      },
      get blockId() {
        return source.blockId;
      },
      get _rowShardAutoRepairAttemptedSnapshot() {
        return source._rowShardAutoRepairAttemptedSnapshot;
      },
      set _rowShardAutoRepairAttemptedSnapshot(value) {
        source._rowShardAutoRepairAttemptedSnapshot = value;
      },
      get _rowShardIntegrityValidationAbort() {
        return source._rowShardIntegrityValidationAbort;
      },
      set _rowShardIntegrityValidationAbort(value) {
        source._rowShardIntegrityValidationAbort = value;
      },
      get _rowShardIntegrityValidationAttemptedSnapshot() {
        return source._rowShardIntegrityValidationAttemptedSnapshot;
      },
      set _rowShardIntegrityValidationAttemptedSnapshot(value) {
        source._rowShardIntegrityValidationAttemptedSnapshot = value;
      },
      get _rowShardIntegrityValidationInFlight() {
        return source._rowShardIntegrityValidationInFlight;
      },
      set _rowShardIntegrityValidationInFlight(value) {
        source._rowShardIntegrityValidationInFlight = value;
      },
      get _rowShardAuthoritativeIntegritySnapshot() {
        return source._rowShardAuthoritativeIntegritySnapshot;
      },
      set _rowShardAuthoritativeIntegritySnapshot(value) {
        source._rowShardAuthoritativeIntegritySnapshot = value;
      },
      get captureStorageSync() {
        return source.captureStorageSync.bind(source);
      },
      get _rowShardIntegrityError() {
        return source._rowShardIntegrityError;
      },
      get _rowShardDocLoadDisposers() {
        return source._rowShardDocLoadDisposers;
      },
      get _shardedReadyObservedDocs() {
        return source._shardedReadyObservedDocs;
      },
      get _shardedReadyDisposers() {
        return source._shardedReadyDisposers;
      },
      get _shardedObservedDocs() {
        return source._shardedObservedDocs;
      },
      get _shardedDocObserverDisposers() {
        return source._shardedDocObserverDisposers;
      },
      get _activeShardedWindowDocs() {
        return source._activeShardedWindowDocs;
      },
      set _activeShardedWindowDocs(value) {
        source._activeShardedWindowDocs = value;
      },
      get _shardedRowsByDocId() {
        return source._shardedRowsByDocId;
      },
      get _shardedSearchTextByRowId() {
        return source._shardedSearchTextByRowId;
      },
      get isSpacialProperty() {
        return source.isSpacialProperty.bind(source);
      },
      get _shardedSearchPropertySignature() {
        return source._shardedSearchPropertySignature;
      },
      set _shardedSearchPropertySignature(value) {
        source._shardedSearchPropertySignature = value;
      },
      get assertFileIndexRootSchemaWithinLimits() {
        return source.assertFileIndexRootSchemaWithinLimits.bind(source);
      },
      get _publishingFileIndexShardManifest() {
        return source._publishingFileIndexShardManifest;
      },
      set _publishingFileIndexShardManifest(value) {
        source._publishingFileIndexShardManifest = value;
      },
      get _fileIndexManifestReconcileQueued() {
        return source._fileIndexManifestReconcileQueued;
      },
      set _fileIndexManifestReconcileQueued(value) {
        source._fileIndexManifestReconcileQueued = value;
      },
      get _fileIndexShardRegistryDisposer() {
        return source._fileIndexShardRegistryDisposer;
      },
      set _fileIndexShardRegistryDisposer(value) {
        source._fileIndexShardRegistryDisposer = value;
      },
      get _fileIndexWriterShardId() {
        return source._fileIndexWriterShardId;
      },
      set _fileIndexWriterShardId(value) {
        source._fileIndexWriterShardId = value;
      },
      get assertFileIndexCellMutationAllowed() {
        return source.assertFileIndexCellMutationAllowed.bind(source);
      },
      get assertFileIndexRowMutationAllowed() {
        return source.assertFileIndexRowMutationAllowed.bind(source);
      },
      get doc() {
        return source.doc;
      },
      get assertNoLocalRootRowMutation() {
        return source.assertNoLocalRootRowMutation.bind(source);
      },
      get rowCount$() {
        return source.rowCount$;
      },
      get _reactiveShardedCellValues() {
        return source._reactiveShardedCellValues;
      },
      get _reactiveShardedCellFrames() {
        return source._reactiveShardedCellFrames;
      },
      get _batch() {
        return source._batch;
      },
      set _batch(value) {
        source._batch = value;
      },
      get getBoundedPropertyIdsWindow() {
        return source.getBoundedPropertyIdsWindow.bind(source);
      },
      get columnCount$() {
        return source.columnCount$;
      },
      get sourceRowIds$() {
        return source.sourceRowIds$;
      },
      get spacialValueGet() {
        return source.spacialValueGet.bind(source);
      },
      get isFixedProperty() {
        return source.isFixedProperty.bind(source);
      },
      get workspaceId() {
        return source.workspaceId;
      },
      get docId() {
        return source.docId;
      },
      get viewDataList$() {
        return source.viewDataList$;
      },
      get pruneLocalUiStateForDeletedView() {
        return source.pruneLocalUiStateForDeletedView.bind(source);
      },
      getUnshardedProjectionSource: query => super.getProjectionSource(query),
      getUnshardedCellsWindow: request => super.getCellsWindow(request),
    });
  }

  private readonly localUi = new DatabaseLocalUiController({
    rootDocId: () =>
      this._model.props.databaseRootDocId ?? this._model.store.id,
    databaseId: () => this._databaseIdentity.databaseId,
    viewManager: () => this.databaseViewManager,
    rowIdsForLayout: () => this.getTableLayoutRowIdsForFiltering(),
  });

  override get parentProvider() {
    return this._hostModel.store.provider;
  }

  // Streamlain: Expose database block ID for bulk upload and other features
  get blockId(): string {
    return this._hostModel.id;
  }

  get databaseId(): string {
    return this._databaseIdentity.databaseId;
  }

  get docId(): string {
    return this._hostModel.store.id;
  }

  get workspaceId(): string {
    return this._hostModel.store.workspace.id;
  }

  private isFileIndexDatabase(): boolean {
    return this._model.props.managedBy === FILE_INDEX_MANAGED_BY;
  }

  private assertFileIndexRootSchemaWithinLimits(overrides: {
    columns?: unknown;
    manifest?: unknown;
    registryShards?: readonly DatabaseShardManifestEntry[];
    views?: unknown;
  }): void {
    if (!this.isFileIndexDatabase()) return;
    const views = overrides.views ?? this._model.props.views;
    if (Array.isArray(views) && views.length > FILE_INDEX_VIEW_HARD_LIMIT) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Files database supports at most ${FILE_INDEX_VIEW_HARD_LIMIT} views.`
      );
    }
    const registryShards =
      overrides.registryShards ??
      sortFileIndexShardEntries(
        getFileIndexShardRegistry(
          this._model.store.doc.spaceDoc,
          this.databaseId
        ).values()
      );
    const encodedBytes = new TextEncoder().encode(
      JSON.stringify({
        columns: overrides.columns ?? this._model.props.columns,
        databaseShardManifest:
          overrides.manifest ?? this._model.props.databaseShardManifest,
        shardRegistry: registryShards,
        views,
      })
    ).byteLength;
    if (encodedBytes > FILE_INDEX_ROOT_SCHEMA_HARD_LIMIT_BYTES) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Files database root schema exceeds ${FILE_INDEX_ROOT_SCHEMA_HARD_LIMIT_BYTES} bytes.`
      );
    }
  }

  private fileIndexRowsManagedReason(): string {
    return 'Rows in the Files database are created from canvas files.';
  }

  override rowCanAdd(): boolean {
    return !this.isFileIndexDatabase();
  }

  override rowAddDisabledReason(): string | undefined {
    return this.isFileIndexDatabase()
      ? this.fileIndexRowsManagedReason()
      : undefined;
  }

  private isFileIndexSystemProperty(propertyId: string): boolean {
    if (!this.isFileIndexDatabase()) return false;
    return (
      isFileIndexSystemColumnId(propertyId) ||
      isFileIndexManagedColumnData(this.propertyDataGet(propertyId))
    );
  }

  private assertFileIndexRowMutationAllowed(
    actor: DataSourceWriteActor,
    operation: string
  ): void {
    if (!this.isFileIndexDatabase() || actor === 'system') return;
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `${operation} is not allowed: ${this.fileIndexRowsManagedReason()}`
    );
  }

  private assertFileIndexCellMutationAllowed(
    propertyId: string,
    actor: DataSourceWriteActor
  ): void {
    if (!this.isFileIndexSystemProperty(propertyId) || actor === 'system') {
      return;
    }
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Column "${propertyId}" is managed by the Files database. Create a user column for manual or automation writes.`
    );
  }

  get isRowSharded(): boolean {
    const manifest = normalizeDatabaseShardManifest(
      this._model.props.databaseShardManifest
    );
    if (!manifest) {
      if (hasLegacyInlineDatabaseStorageModel(this._model)) {
        return false;
      }
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database "${this.databaseId}" has no valid row-shard manifest.`
      );
    }
    return true;
  }

  private assertNoLocalRootRowMutation(action: string): void {
    // sl-d7zo.7.4: Row-sharded databases must never mutate root `prop:cells`.
    // Interactive database writes must use owning-doc metadata or row-shard
    // docs as their local-first source of truth.
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `${action} cannot write database rows into the owning document; ${ROW_SHARDED_DATABASE_GATE_MESSAGE}.`
    );
  }

  spacialProperties: Record<string, SpacialProperty> = {
    'created-time': {
      valueSet: () => {},
      valueGet: (rowId: string) => {
        const model = this.getModelById(rowId) as ParagraphBlockModel;
        if (!model) {
          return null;
        }
        return model.props['meta:createdAt'];
      },
    },
    'created-by': {
      valueSet: () => {},
      valueGet: (rowId: string) => {
        const model = this.getModelById(rowId) as
          | ParagraphBlockModel
          | undefined;
        if (!model) {
          return null;
        }
        return model.props['meta:createdBy'] ?? null;
      },
    },
    'updated-time': {
      valueSet: () => {},
      valueGet: (rowId: string) => {
        const model = this.getModelById(rowId) as ParagraphBlockModel;
        if (!model) {
          return null;
        }
        return model.props['meta:updatedAt'];
      },
    },
    'updated-by': {
      valueSet: () => {},
      valueGet: (rowId: string) => {
        const model = this.getModelById(rowId) as
          | ParagraphBlockModel
          | undefined;
        return model ? model.props['meta:updatedBy'] : null;
      },
    },
    type: {
      valueSet: () => {},
      valueGet: (rowId: string) => {
        const model = this.getModelById(rowId);
        if (!model) {
          return;
        }
        return getIcon(model);
      },
    },
    title: {
      valueSet: () => {},
      valueGet: (rowId: string) => {
        const model = this.getModelById(rowId);
        if (!model) {
          return;
        }
        return model.text;
      },
    },
  };

  isSpacialProperty(propertyType: string): boolean {
    return this.spacialProperties[propertyType] !== undefined;
  }

  spacialValueGet(
    rowId: string,
    propertyId: string,
    propertyType: string
  ): unknown {
    return this.spacialProperties[propertyType]?.valueGet(rowId, propertyId);
  }

  static externalProperties = signal<PropertyMetaConfig[]>([]);
  static propertiesList = computed(() => {
    return [
      ...Object.values(databaseBlockProperties),
      ...this.externalProperties.value,
    ];
  });
  static propertiesMap = computed(() => {
    return Object.fromEntries(
      this.propertiesList.value.map(v => [v.type, v as PropertyMetaConfig])
    );
  });

  static registerRowShardDocLoader(
    store: object,
    loader: DatabaseRowShardDocLoader
  ): () => void {
    let loaders = rowShardDocLoaders.get(store);
    if (!loaders) {
      loaders = new Set();
      rowShardDocLoaders.set(store, loaders);
    }
    loaders.add(loader);
    return () => {
      loaders?.delete(loader);
      if (loaders?.size === 0) {
        rowShardDocLoaders.delete(store);
      }
    };
  }

  static registerRowShardSnapshotBarrier(
    store: object,
    barrier: DatabaseRowShardSnapshotBarrier
  ): () => void {
    let barriers = rowShardSnapshotBarriers.get(store);
    if (!barriers) {
      barriers = new Set();
      rowShardSnapshotBarriers.set(store, barriers);
    }
    barriers.add(barrier);
    return () => {
      barriers?.delete(barrier);
      if (barriers?.size === 0) {
        rowShardSnapshotBarriers.delete(store);
      }
    };
  }

  private _batch = 0;

  private _disposed = false;

  private _fileIndexShardRegistryDisposer?: () => void;

  private _fileIndexManifestReconcileQueued = false;

  private _fileIndexWriterShardId?: string;

  private _publishingFileIndexShardManifest = false;

  private readonly _hostModel: DatabaseBlockModel;

  private readonly _model: DatabaseBlockModel;

  private readonly _databaseIdentity: ResolvedDatabaseRuntimeIdentity;

  override featureFlags$: ReadonlySignal<DatabaseFlags> = computed(() => {
    const featureFlagService = this._hostModel.store.get(FeatureFlagService);
    const enableNumberFormat = featureFlagService.getFlag(
      'enable_database_number_formatting'
    );
    const enableTableVirtualScroll = featureFlagService.getFlag(
      'enable_table_virtual_scroll'
    );
    return {
      enable_number_formatting: enableNumberFormat ?? false,
      enable_table_virtual_scroll: enableTableVirtualScroll ?? false,
      use_shared_canvas_viewport: false,
    };
  });

  protected override sourcePropertyIds$: ReadonlySignal<string[]> = computed(
    () => {
      const fixedPropertiesSet = new Set(this.fixedProperties$.value);
      const properties: string[] = [];
      this._model.props.columns$.value.forEach(column => {
        if (fixedPropertiesSet.has(column.type)) {
          fixedPropertiesSet.delete(column.type);
        }
        properties.push(column.id);
      });

      const result = [...fixedPropertiesSet, ...properties];
      return result;
    }
  );

  readonly$: ReadonlySignal<boolean> = computed(() => {
    return this._hostModel.store.readonly;
  });

  /** sl-9qm.21.3: true when database is system-managed (no manual edits) */
  override systemManaged$: ReadonlySignal<boolean> = computed(() => {
    return this._model.props.systemManaged === true;
  });

  /** sl-9qm.21.3: true when system-managed DB allows row deletion (moderation) */
  override allowDelete$: ReadonlySignal<boolean> = computed(() => {
    return this._model.props.allowDelete === true;
  });

  /** sl-web-9n8: true when column structure is locked (rows still editable) */
  schemaLocked$: ReadonlySignal<boolean> = computed(() => {
    return this._model.props.schemaLocked === true;
  });

  private _shardedRefreshFrame: ReturnType<
    typeof requestAnimationFrame
  > | null = null;

  private readonly _shardedObservedDocs = new Set<string>();

  private _activeShardedWindowDocs = new Set<string>();

  private readonly _shardedReadyObservedDocs = new Set<string>();

  private readonly _shardedDocObserverDisposers = new Map<string, () => void>();

  private readonly _shardedReadyDisposers = new Map<string, () => void>();

  private readonly _rowShardDocLoadDisposers = new Map<
    string,
    Array<() => void>
  >();

  private readonly _shardedRowsRevision = signal(0);

  private readonly _rowShardIntegrityError = signal<string | null>(null);

  private _rowShardAutoRepairAbort?: AbortController;

  private _rowShardAutoRepairAttemptedSnapshot?: string;

  private _rowShardAutoRepairInFlight?: Promise<void>;

  private _rowShardIntegrityValidationAbort?: AbortController;

  private _rowShardIntegrityValidationAttemptedSnapshot?: string;

  private _rowShardIntegrityValidationInFlight?: Promise<void>;

  private _rowShardAuthoritativeIntegritySnapshot?: string;

  private _rowShardEmptyConfirmationAttemptedSnapshot?: string;

  private _rowShardEmptyConfirmedSnapshot?: string;

  private _shardedRowIds: string[] = [];

  private _shardedRowsById = new Map<string, ShardedRowRecord>();

  private readonly _shardedRowsByDocId = new Map<
    string,
    ShardedRowsDocCacheEntry
  >();

  private readonly _shardedSearchTextByRowId = new Map<string, string>();

  private _shardedSearchPropertySignature = '';

  private readonly _reactiveShardedCellValues = new Map<
    string,
    ReactiveShardedCellValue
  >();

  private readonly _reactiveShardedCellFrames = new Map<
    string,
    ReturnType<typeof requestAnimationFrame>
  >();

  protected override sourceRowIds$: ReadonlySignal<string[]> = computed(() => {
    if (this.isRowSharded) {
      this._shardedRowsRevision.value;
      return this._shardedRowIds.slice();
    }
    return this._model.children.map(v => v.id);
  });

  override rowCount$: ReadonlySignal<number> = computed(() => {
    const manifest = normalizeDatabaseShardManifest(
      this._model.props.databaseShardManifest
    );
    if (manifest) {
      this._shardedRowsRevision.value;
      return this.getManifestRowCount(manifest);
    }
    return this.sourceRowIds$.value.length;
  });

  ensureRelationRowDataLoaded(): void {
    const manifest = normalizeDatabaseShardManifest(
      this._model.props.databaseShardManifest
    );
    if (!manifest) return;
    for (const shard of manifest.shards) {
      this.ensureShardObserved(shard, 'relation');
    }
  }

  getRelationRowDataLoadState(): 'loading' | 'ready' {
    const manifest = normalizeDatabaseShardManifest(
      this._model.props.databaseShardManifest
    );
    if (!manifest) return 'ready';

    this._shardedRowsRevision.value;
    this.refreshShardedRowsFromDocs(manifest.shards);

    return this.areAllManifestShardRowsMaterialized(manifest)
      ? 'ready'
      : 'loading';
  }

  /**
   * Resolve a search result against its authoritative row-shard snapshot
   * without materializing every shard in the database.
   *
   * The row id remains the stable semantic identity. The shard document id is
   * only a current locator and is validated against the loaded shard before an
   * absolute position is returned.
   */
  resolveRowShardAbsoluteIndex(
    rowId: string,
    rowShardDocId: string
  ): number | null {
    const manifest = normalizeDatabaseShardManifest(
      this._model.props.databaseShardManifest
    );
    if (!manifest) {
      throw new Error(
        `Database row shard manifest is missing: database=${this._model.id} row=${rowId}`
      );
    }

    const shardIndex = manifest.shards.findIndex(
      shard => shard.serverDocId === rowShardDocId
    );
    if (shardIndex < 0) {
      throw new Error(
        `Database row shard locator is stale: database=${this._model.id} row=${rowId} shard=${rowShardDocId}`
      );
    }

    const shard = manifest.shards[shardIndex]!;
    const shardDoc = this.ensureShardObserved(shard, 'row-window');
    if (!shardDoc.ready || !this.hasCompleteLoadedShardRows(shard, shardDoc)) {
      return null;
    }

    const localIndex = shardDoc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray()
      .map(value => String(value))
      .indexOf(rowId);
    if (localIndex < 0) {
      throw new Error(
        `Database row is absent from its indexed shard: database=${this._model.id} row=${rowId} shard=${rowShardDocId}`
      );
    }

    return (
      manifest.shards
        .slice(0, shardIndex)
        .reduce((total, entry) => total + entry.rowCount, 0) + localIndex
    );
  }

  viewConverts = databaseBlockViewConverts;

  viewDataList$: ReadonlySignal<DataViewDataType[]> = computed(() => {
    return getOrderedViews(
      this._model.props.views$.value as DataViewDataType[]
    ).map(getViewDataForRead);
  });

  private readonly databaseViewManager: DatabaseBlockViewManager;

  override viewManager: ViewManager;

  get viewMetas() {
    return getCreatableDatabaseBlockViews();
  }

  get doc() {
    return this._hostModel.store;
  }

  get storageModel() {
    return this._model;
  }

  private get storageDoc() {
    return this._model.store;
  }

  private captureStorageSync(): void {
    this.storageDoc.captureSync();
  }

  allPropertyMetas$ = computed<PropertyMetaConfig<any, any, any, any>[]>(() => {
    return DatabaseBlockDataSource.propertiesList.value;
  });

  propertyMetas$ = computed<PropertyMetaConfig[]>(() => {
    return this.allPropertyMetas$.value.filter(
      v => !v.config.fixed && !v.config.hide
    );
  });

  constructor(
    model: DatabaseBlockModel,
    init?: (dataSource: DatabaseBlockDataSource) => void
  ) {
    super();
    this._hostModel = model;
    const legacyInlineStorage = hasLegacyInlineDatabaseStorageModel(model);
    assertDatabaseRuntimeIdentity(model, legacyInlineStorage);
    this._model = resolveDatabaseStorageModel(model);
    this._databaseIdentity = assertDatabaseRuntimeIdentity(
      this._model,
      legacyInlineStorage && this._model === model
    );
    this.databaseViewManager = new DatabaseBlockViewManager(this, this._model);
    this.viewManager = this.databaseViewManager;
    this.initializeFileIndexShardRegistry();
    this.registerSystemDocAccessBinding();
    init?.(this); // then allow external initialisation
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    this._rowShardAutoRepairAbort?.abort();
    this._rowShardAutoRepairAbort = undefined;
    this._rowShardAutoRepairInFlight = undefined;
    this._rowShardIntegrityValidationAbort?.abort();
    this._rowShardIntegrityValidationAbort = undefined;
    this._rowShardIntegrityValidationInFlight = undefined;
    this._rowShardEmptyConfirmationAttemptedSnapshot = undefined;
    this._rowShardEmptyConfirmedSnapshot = undefined;

    this._fileIndexShardRegistryDisposer?.();
    this._fileIndexShardRegistryDisposer = undefined;

    for (const dispose of this._shardedReadyDisposers.values()) {
      dispose();
    }
    this._shardedReadyDisposers.clear();
    this._shardedReadyObservedDocs.clear();

    for (const dispose of this._shardedDocObserverDisposers.values()) {
      dispose();
    }
    this._shardedDocObserverDisposers.clear();
    this._shardedObservedDocs.clear();
    this._activeShardedWindowDocs.clear();
    this._shardedRowsByDocId.clear();

    for (const disposers of this._rowShardDocLoadDisposers.values()) {
      for (const dispose of disposers) {
        dispose();
      }
    }
    this._rowShardDocLoadDisposers.clear();

    if (this._shardedRefreshFrame !== null) {
      cancelAnimationFrame(this._shardedRefreshFrame);
      this._shardedRefreshFrame = null;
    }

    for (const frame of this._reactiveShardedCellFrames.values()) {
      cancelAnimationFrame(frame);
    }
    this._reactiveShardedCellFrames.clear();

    for (const entry of this._reactiveShardedCellValues.values()) {
      entry.dispose();
    }
    this._reactiveShardedCellValues.clear();

    if (this._batch) {
      cancelAnimationFrame(this._batch);
      this._batch = 0;
    }
  }

  setUiStatePersistence(mode: DatabaseUiStatePersistence): void {
    return this.localUi.setUiStatePersistence(mode);
  }

  persistLocalCurrentView(viewId: string | undefined): void {
    return this.localUi.persistLocalCurrentView(viewId);
  }

  setTransientCurrentView(id: string | undefined): void {
    return this.localUi.setTransientCurrentView(id);
  }

  override getViewWindowState(
    viewId: string
  ): DataSourceViewWindowState | undefined {
    return this.localUi.getViewWindowState(viewId);
  }

  override setViewWindowState(
    viewId: string,
    patch: DataSourceViewWindowState
  ): boolean {
    return this.localUi.setViewWindowState(viewId, patch);
  }

  override getViewLayoutState(
    viewId: string,
    viewMode: string
  ): DataSourceViewLayoutState | undefined {
    return this.localUi.getViewLayoutState(viewId, viewMode);
  }

  override setViewLayoutState(
    viewId: string,
    state: DataSourceViewLayoutState
  ): boolean {
    return this.localUi.setViewLayoutState(viewId, state);
  }

  override getTableViewLayoutState(
    viewId: string,
    columnLayoutSignature: string
  ): DataSourceTableViewLayoutState | undefined {
    return this.localUi.getTableViewLayoutState(viewId, columnLayoutSignature);
  }

  override setTableViewLayoutState(
    viewId: string,
    state: DataSourceTableViewLayoutState
  ): boolean {
    return this.localUi.setTableViewLayoutState(viewId, state);
  }

  private getTableLayoutRowIdsForFiltering(): Set<string> | undefined {
    if (!this.isRowSharded) {
      return new Set(this.sourceRowIds$.value);
    }

    const manifest = normalizeDatabaseShardManifest(
      this._model.props.databaseShardManifest
    );
    if (!manifest) {
      return new Set(this.sourceRowIds$.value);
    }

    this._shardedRowsRevision.value;
    const expectedRows = this.getManifestRowCount(manifest);
    if (
      expectedRows > 0 &&
      (this._shardedRowIds.length !== expectedRows ||
        this._shardedRowsById.size !== expectedRows)
    ) {
      return undefined;
    }

    return new Set(this._shardedRowIds);
  }

  private pruneLocalUiStateForDeletedView(
    viewId: string,
    nextViewId: string | undefined
  ): void {
    return this.localUi.pruneLocalUiStateForDeletedView(viewId, nextViewId);
  }

  private getManifestRowCount(
    manifest: ReturnType<typeof normalizeDatabaseShardManifest>
  ): number {
    return this.runtime.getManifestRowCount(manifest);
  }

  getInitialRowWindowReadiness(
    limit: number = INITIAL_DATABASE_ROW_WINDOW_LIMIT
  ): DatabaseRowShardReadiness {
    return this.runtime.getInitialRowWindowReadiness(limit);
  }

  getContentReadiness(): DatabaseRowShardReadiness {
    return this.runtime.getContentReadiness();
  }

  isInitialRowWindowReady(
    limit: number = INITIAL_DATABASE_ROW_WINDOW_LIMIT
  ): boolean {
    return this.runtime.isInitialRowWindowReady(limit);
  }

  repairLoadedRowShardManifestDrift(
    expectedSnapshot?: string
  ): DatabaseRowShardRepairResult {
    return this.runtime.repairLoadedRowShardManifestDrift(expectedSnapshot);
  }

  private areAllManifestShardRowsMaterialized(
    manifest: ReturnType<typeof normalizeDatabaseShardManifest>
  ): boolean {
    return this.runtime.areAllManifestShardRowsMaterialized(manifest);
  }

  private hasCompleteLoadedShardRows(
    shard: DatabaseShardManifestEntry,
    doc: DatabaseShardDocument | null | undefined
  ): boolean {
    return this.runtime.hasCompleteLoadedShardRows(shard, doc);
  }

  private ensureShardObserved(
    shard: DatabaseShardManifestEntry,
    reason: DatabaseRowShardDocLoadReason = 'row-window'
  ): DatabaseShardDocument {
    return this.runtime.ensureShardObserved(shard, reason);
  }

  private refreshShardedRowsFromDocs(
    shards?: readonly DatabaseShardManifestEntry[]
  ): void {
    return this.runtime.refreshShardedRowsFromDocs(shards);
  }

  private initializeFileIndexShardRegistry(): void {
    return this.runtime.initializeFileIndexShardRegistry();
  }

  registerSystemDocAccessBinding(attempt = 0): void {
    return this.runtime.registerSystemDocAccessBinding(attempt);
  }

  rowAddWithId(
    rowId: string,
    insertPosition: InsertToPosition | number,
    actor: DataSourceWriteActor = 'user'
  ): { rowId: string; created: boolean } {
    return this.runtime.rowAddWithId(rowId, insertPosition, actor);
  }

  rowAddManyWithCellValues(
    rows: readonly DatabaseRowCellValueInsert[],
    insertPosition: 'end',
    actor: DataSourceWriteActor = 'user'
  ): number {
    return this.runtime.rowAddManyWithCellValues(rows, insertPosition, actor);
  }

  private getModelById(rowId: string): BlockModel | undefined {
    return this.runtime.getModelById(rowId);
  }

  override getRowsWindow(request: {
    offset?: number;
    limit: number;
  }): DataSourceRowsWindow {
    return this.runtime.getRowsWindow(request);
  }

  override getProjectionSource(
    query: DatabaseProjectionQuery
  ): DatabaseProjectionSource {
    return this.runtime.getProjectionSource(query);
  }

  override getSearchCandidateRowIds(
    rowIds: readonly string[],
    searchText: string,
    propertyIds: readonly string[]
  ): readonly string[] {
    return this.runtime.getSearchCandidateRowIds(
      rowIds,
      searchText,
      propertyIds
    );
  }

  override getCellsWindow(
    request: DataSourceCellsWindowRequest
  ): DataSourceCellsWindow {
    return this.runtime.getCellsWindow(request);
  }

  override getLogicalRowIdsSnapshot(): string[] {
    return this.runtime.getLogicalRowIdsSnapshot();
  }

  override rowExists(rowId: string): boolean {
    return this.runtime.rowExists(rowId);
  }

  cellValueChange(
    rowId: string,
    propertyId: string,
    value: unknown,
    actor: DataSourceWriteActor = 'user'
  ): void {
    return this.runtime.cellValueChange(rowId, propertyId, value, actor);
  }

  /**
   * Applies one logical row update as one row-shard transaction. Consumers
   * must not observe a partially written row while a multi-field form save is
   * propagating between independent data-source instances.
   */
  cellValuesChange(
    rowId: string,
    values: Readonly<Record<string, unknown>>,
    actor: DataSourceWriteActor = 'user'
  ): void {
    return this.runtime.cellValuesChange(rowId, values, actor);
  }

  /**
   * sl-d7zo.7.4: Sharded local-first cell-value write path.
   *
   * 1. Normalise the incoming value through the property meta (so
   *    select/multi-select option lookups + schema-locked guards still
   *    fire — same logic as the local path).
   * 2. Write the owning row-shard subdoc as the single interactive source of
   *    truth. nbstore sync persists that local Yjs update.
   *
   * The GraphQL mutation path is intentionally not used here. Sending a second
   * server-authoritative write for every keystroke races remote echoes against
   * newer local text input and can roll back the last typed character.
   */

  cellValueGet(rowId: string, propertyId: string): unknown {
    return this.runtime.cellValueGet(rowId, propertyId);
  }

  propertyAdd(
    insertToPosition: InsertToPosition,
    ops?: {
      type?: string;
      name?: string;
    }
  ): string | undefined {
    return this.runtime.propertyAdd(insertToPosition, ops);
  }

  protected override getNormalPropertyAndIndex(propertyId: string):
    | {
        column: ColumnDataType<Record<string, unknown>>;
        index: number;
      }
    | undefined {
    return this.runtime.getNormalPropertyAndIndex(propertyId);
  }

  propertyDataGet(propertyId: string): Record<string, unknown> {
    return this.runtime.propertyDataGet(propertyId);
  }

  propertyDataSet(propertyId: string, data: Record<string, unknown>): void {
    return this.runtime.propertyDataSet(propertyId, data);
  }

  propertyDataTypeGet(propertyId: string): TypeInstance | undefined {
    return this.runtime.propertyDataTypeGet(propertyId);
  }

  propertyDelete(id: string): void {
    return this.runtime.propertyDelete(id);
  }

  propertyDuplicate(propertyId: string): string | undefined {
    return this.runtime.propertyDuplicate(propertyId);
  }

  /** sl-web-9n8: Block column operations when schema is locked */

  override propertyCanDelete(propertyId: string): boolean {
    return this.runtime.propertyCanDelete(propertyId);
  }

  override propertyCanDuplicate(propertyId: string): boolean {
    return this.runtime.propertyCanDuplicate(propertyId);
  }

  override propertyTypeCanSet(propertyId: string): boolean {
    return this.runtime.propertyTypeCanSet(propertyId);
  }

  propertyMetaGet(type: string): PropertyMetaConfig | undefined {
    return this.runtime.propertyMetaGet(type);
  }

  propertyNameGet(propertyId: string): string {
    return this.runtime.propertyNameGet(propertyId);
  }

  propertyNameSet(propertyId: string, name: string): void {
    return this.runtime.propertyNameSet(propertyId, name);
  }

  override propertyReadonlyGet(propertyId: string): boolean {
    return this.runtime.propertyReadonlyGet(propertyId);
  }

  override propertyValueReadonlyGet(propertyId: string): boolean {
    if (
      this.schemaLocked$.value &&
      isSystemManagedPropertyData(this.propertyDataGet(propertyId))
    ) {
      const readonlyProperties = new Set([
        'type',
        'created-time',
        'created-by',
        'updated-time',
        'updated-by',
      ]);
      const propertyType = this.propertyTypeGet(propertyId);
      return (
        readonlyProperties.has(propertyId) ||
        (propertyType != null && readonlyProperties.has(propertyType))
      );
    }
    return this.propertyReadonlyGet(propertyId);
  }

  propertyTypeGet(propertyId: string): string | undefined {
    return this.runtime.propertyTypeGet(propertyId);
  }

  propertyTypeSet(propertyId: string, toType: string): void {
    return this.runtime.propertyTypeSet(propertyId, toType);
  }

  rowAdd(insertPosition: InsertToPosition | number): string {
    return this.runtime.rowAdd(insertPosition);
  }

  rowDelete(ids: string[], actor: DataSourceWriteActor = 'user'): void {
    return this.runtime.rowDelete(ids, actor);
  }

  rowMove(rowId: string, position: InsertToPosition): void {
    return this.runtime.rowMove(rowId, position);
  }

  viewDataAdd(viewData: DataViewDataType): string {
    return this.runtime.viewDataAdd(viewData);
  }

  viewDataDelete(viewId: string): void {
    return this.runtime.viewDataDelete(viewId);
  }

  viewDataDuplicate(id: string): string {
    return this.runtime.viewDataDuplicate(id);
  }

  viewDataGet(viewId: string): DataViewDataType | undefined {
    return this.runtime.viewDataGet(viewId);
  }

  viewDataMoveTo(id: string, position: InsertToPosition): void {
    return this.runtime.viewDataMoveTo(id, position);
  }

  viewDataUpdate<ViewData extends DataViewDataType>(
    id: string,
    updater: (data: ViewData) => Partial<ViewData>
  ): void {
    return this.runtime.viewDataUpdate(id, updater);
  }

  viewMetaGet(type: string): ViewMeta {
    return this.runtime.viewMetaGet(type);
  }

  viewMetaGetById(viewId: string): ViewMeta | undefined {
    return this.runtime.viewMetaGetById(viewId);
  }
}

export function createDatabaseBlockDataSource(
  model: DatabaseBlockModel,
  init?: (dataSource: DatabaseBlockDataSource) => void
): DatabaseBlockDataSource {
  return new DatabaseBlockDataSource(model, init);
}

export function provisionShardedDatabaseBlockDataSource(
  hostDoc: Store,
  input: ShardedDatabaseBlockDataSourceProvisionInput
): ShardedDatabaseBlockDataSourceProvisionResult {
  const provisioned = provisionShardedDatabaseHostBlock(hostDoc, input);
  const dataSource = createDatabaseBlockDataSource(provisioned.model);
  input.initDataSource?.(dataSource);
  return {
    ...provisioned,
    dataSource,
  };
}

export const databaseViewInitTemplate = (
  datasource: DatabaseBlockDataSource,
  viewType: string
) => {
  datasource.isRowSharded;
  datasource.viewManager.viewAdd(viewType);
};
export const convertToDatabase = (host: EditorHost, viewType: string) => {
  const [_, ctx] = host.std.command.exec(getSelectedModelsCommand, {
    types: ['block', 'text'],
  });
  const { selectedModels } = ctx;
  const firstModel = selectedModels?.[0];
  if (!firstModel) return;

  host.store.captureSync();

  const parentModel = host.store.getParent(firstModel);
  if (!parentModel) {
    return;
  }

  assertDatabaseColumnShapeWithinLimits({
    source: 'Converted database',
    columnCount: 1,
  });
  if (selectedModels.length > 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Converted database cannot move selected blocks into database rows; ${ROW_SHARDED_DATABASE_GATE_MESSAGE}.`
    );
  }

  const provisioned = provisionShardedDatabaseBlockDataSource(host.store, {
    title: 'Untitled Database',
    addBlock: (_flavour, props) =>
      host.store.addBlock(
        'affine:database',
        props,
        parentModel,
        parentModel.children.indexOf(firstModel)
      ),
    initDataSource: dataSource => dataSource.viewManager.viewAdd(viewType),
  });
  const databaseModel = provisioned.model;
  host.store.moveBlocks(selectedModels, databaseModel);

  const selectionManager = host.selection;
  selectionManager.clear();
};
